import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { SchemaProviderInstance } from "../@shared/services/SchemaProvider";
import { onThrowAlert } from "../sagas/dataSaga/dataSagaActions";
import { NotificationModel, NotificationModelType } from "../reducers/main/mainModel";

export type UseSchemaReadmeResult = {
    onUpdateSchemaReadme: (id: string, content: string) => Promise<void>;
    onLoadSchemaReadme: (id: string) => Promise<void>;
    onChangeContent: (content: string) => void;
    isProcessing: boolean;
    isLoading: boolean;
    content: string;
    isUpdated: boolean;
}

export const useSchemaReadme = (): UseSchemaReadmeResult => {
    const dispatch = useDispatch();

    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [content, setContent] = useState("");
    const [isUpdated, setIsUpdated] = useState(false);

    const onChangeContent = useCallback((content: string) => {
        setContent(content);
        setIsUpdated(true);
    }, []);

    const onLoadSchemaReadme = useCallback(async (id: string) => {
        try {
            setIsLoading(true);
            const result = await SchemaProviderInstance.getReadme(id);
            if (result.status === 200) {
                setContent(result.data.content ?? "");
            }
            else if (result.status === 404) {
                setContent("");
            } else {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
        }
        catch (error) {
            dispatch(onThrowAlert({
                message: (error as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsLoading(false);
        }

    }, [dispatch])

    const onUpdateSchemaReadme = useCallback(async (id: string, contentToSave: string) => {
        try {
            setIsProcessing(true);
            const result = await SchemaProviderInstance.updateReadme(id, contentToSave);
            if (result.status === 200) {
                dispatch(onThrowAlert({
                    message: "Readme was updated",
                    type: NotificationModelType.Success,
                    lifeTime: 10000
                } as NotificationModel));
            } else {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
        }
        catch (error) {
            dispatch(onThrowAlert({
                message: (error as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsProcessing(false);
        }

    }, [dispatch])

    return {
        onLoadSchemaReadme,
        onUpdateSchemaReadme,
        onChangeContent,
        isProcessing,
        isLoading,
        content,
        isUpdated
    }
}